import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

const privacyPillars = [
  {
    icon: 'tabler:building-bank',
    title: 'Model-provider isolation',
    body: 'Prompts and files are processed inside AWS; model companies do not receive that traffic.',
  },
  {
    icon: 'tabler:database-off',
    title: 'No training on your data',
    body: 'Inputs and outputs are excluded from provider training and model-improvement programs.',
  },
  {
    icon: 'tabler:shield-lock',
    title: 'Enterprise controls',
    body: 'AES-256 at rest, TLS 1.2+ in transit, role-based access, SSO, and domain restriction.',
  },
  {
    icon: 'tabler:database-export',
    title: 'Your data, your control',
    body: 'Export your information or permanently delete it whenever you choose.',
  },
];

const CampaignPrivacyProof: React.FC = () => (
  <section className="bg-white py-20" aria-labelledby="campaign-privacy-heading">
    <div className="mx-auto max-w-7xl px-6">
      <div className="relative overflow-hidden rounded-[32px] bg-[#04182e] px-7 py-12 text-white sm:px-12 sm:py-14">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff07_1px,transparent_1px),linear-gradient(to_bottom,#ffffff07_1px,transparent_1px)] bg-[size:3rem_3rem]" />
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary-600/30 blur-[90px]" />
          <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-[#d3a641]/10 blur-[90px]" />
        </div>

        <div className="relative">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#ddb85a]">
              Private AI by design
            </p>
            <h2
              id="campaign-privacy-heading"
              className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              Frontier models. Your knowledge stays yours.
            </h2>
            <p className="mt-5 text-base leading-7 text-blue-100/75 sm:text-lg">
              For foundation models accessed through Chat.co on AWS Bedrock, your inputs and the
              AI&apos;s outputs are not shared with model providers and are not used to train or
              improve their models. That protection comes from service terms—not an opt-out toggle.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {privacyPillars.map((pillar) => (
              <article
                key={pillar.title}
                className="rounded-2xl border border-white/10 bg-white/[0.05] p-6">
                <Icon icon={pillar.icon} className="text-2xl text-[#e2c06a]" />
                <h3 className="mt-4 text-base font-bold">{pillar.title}</h3>
                <p className="mt-2 text-sm leading-6 text-blue-100/65">{pillar.body}</p>
              </article>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-7 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-3xl text-xs leading-5 text-blue-100/50">
              Training and provider-sharing commitments reflect AWS Bedrock service terms for
              foundation models accessed through Chat.co.
            </p>
            <Link
              href="/privacy"
              className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-white hover:text-[#e2c06a]">
              See how private AI works
              <Icon icon="tabler:arrow-right" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default CampaignPrivacyProof;
